import { useFetcher } from "~/hooks/useFetcher";
import { PermissionWarningCard } from "~/pages/manifold-nextgen/components/PermissionWarningCard";
import { type AgentShareToolkitAccess, agentShareToolkitAccessPath } from "~/services/agents";
import { groupPrincipal, userPrincipal } from "~/services/principals";
import { CopyAllEmailsButton } from "./CopyAllEmailsButton";
import { listMembers, memberCountPhrase, toolkitPhrase } from "./toolkitAccessCopy";

export interface ShareRecipient {
  type: "user" | "group";
  // The user's email or the group's name.
  id: string;
  name: string;
}

interface AgentShareRecipientWarningProps {
  agentId: string;
  recipient: ShareRecipient;
}

// Warns, before sharing, that the recipient — or some members of a group — lack
// the scopes some of the agent's toolkits need: they'd be able to use the agent
// but not those toolkits. The check is server-side because it needs the
// recipients' scopes, not the caller's.
export const AgentShareRecipientWarning = ({
  agentId,
  recipient,
}: AgentShareRecipientWarningProps) => {
  const principal =
    recipient.type === "user" ? userPrincipal(recipient.id) : groupPrincipal(recipient.id);
  const { data } = useFetcher<AgentShareToolkitAccess>(
    agentShareToolkitAccessPath(agentId, principal)
  );
  const toolkitNames = (data?.toolkits ?? []).map((t) => t.display_name);
  if (!data || toolkitNames.length === 0) {
    return null;
  }

  const who =
    recipient.type === "user"
      ? recipient.name
      : `${memberCountPhrase(data.members_lacking_access.length, data.member_count)} of ${
          recipient.name
        } (${listMembers(data.members_lacking_access)})`;

  return (
    <PermissionWarningCard
      rows={[
        {
          key: "toolkits",
          kind: "agent",
          subject: who,
          text: `won't be able to use this agent's ${toolkitPhrase(
            toolkitNames
          )}. Please reach out to an admin to grant them access.`,
          action: recipient.type === "group" && (
            <CopyAllEmailsButton emails={data.members_lacking_access} />
          ),
        },
      ]}
    />
  );
};
